import moment from 'moment';
import MoblicoApi from 'src/api/MoblicoApi';
import PlatformProvider from 'src/services/PlatformProvider';
type TokenConfig = {
  token: string;
  tokenExpiry: number;
};
export default {
  applicationToken: {} as TokenConfig,
  userTokens: {} as Record<string, Promise<TokenConfig>>,
  lastAuthTime: {} as Record<string, number>,
  invalidUsersUntil: {} as Record<string, number>,
  MIN_AUTH_INTERVAL_MS: 10000,
  INVALID_USER_COOLDOWN_MS: 60000,

  getToken(email?: string) {
    if (email) {
      return this.getUserToken(email);
    }

    return this.getApplicationToken();
  },

  async getUserToken(email: string): Promise<string> {
    const invalidUntil = this.invalidUsersUntil[email];
    if (invalidUntil) {
      const now = Date.now();
      if (now < invalidUntil) {
        return '';
      } else {
        delete this.invalidUsersUntil[email];
        delete this.userTokens[email];
      }
    }

    let userConfigPromise: Promise<TokenConfig> = this.userTokens[email];

    if (!userConfigPromise) {
      userConfigPromise = this.authenticate(email);
    }

    let userConfig = await userConfigPromise;

    if (this.hasTokenExpired(userConfig.tokenExpiry)) {
      userConfigPromise = this.authenticate(email);
    }

    userConfig = await userConfigPromise;
    return userConfig.token;
  },

  async getUserId(email: string): Promise<string> {
    const userToken = await this.getToken(email);
    const userResponse = (await MoblicoApi.getUser(userToken, email)) as {
      id: string;
    };
    return userResponse.id;
  },

  authenticate(email: string): Promise<TokenConfig> {
    const now = Date.now();
    const lastTime = this.lastAuthTime[email] || 0;
    if (now - lastTime < this.MIN_AUTH_INTERVAL_MS) {
      if (this.userTokens[email]) {
        return this.userTokens[email];
      }
    }
    this.lastAuthTime[email] = now;

    const promise = MoblicoApi.authenticate(
      email,
      PlatformProvider.getPlatformName(),
    )
      .then((result: any) => {
        delete this.invalidUsersUntil[email];
        return result as TokenConfig;
      })
      .catch((error) => {
        if (error?.networkResponse?.status === 401) {
          const cooldownUntil = Date.now() + this.INVALID_USER_COOLDOWN_MS;
          this.invalidUsersUntil[email] = cooldownUntil;
          return {token: '', tokenExpiry: 0} as TokenConfig;
        }
        throw error;
      }) as Promise<TokenConfig>;
    this.userTokens[email] = promise;
    return promise;
  },

  async getApplicationToken(): Promise<string> {
    if (
      !this.applicationToken ||
      !this.applicationToken.token ||
      this.hasTokenExpired(this.applicationToken.tokenExpiry)
    ) {
      this.applicationToken = (await MoblicoApi.authenticate()) as TokenConfig;
    }

    return this.applicationToken.token;
  },

  hasTokenExpired(expiry: number) {
    const expiryMoment = moment(expiry);
    return moment().isAfter(expiryMoment);
  },

  clearInvalidUsers() {
    this.invalidUsersUntil = {};
    this.userTokens = {};
    this.lastAuthTime = {};
  },
};
